import {CollectionViewer, DataSource} from '@angular/cdk/collections';
import {BehaviorSubject, Observable,} from 'rxjs';
import {Sort, ValgSortDir, ValgSortFunc} from './valg-sort.directive';
import {Pagination} from '@valg-shared/components/table/table/valg-paginator/valg-paginator.component';
import {
  TableFilterDialogColumnData,
  TableFilterDialogData,
  TableFilterDialogResult,
} from '@valg-shared/components/table/table/valg-filter-dialog/table-filter-dialog.data';
import {Column} from '@valg-shared/components/table/table/column';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {getValueAt, getValueAtWithArrays,} from '@valg-shared/utils/object-utils';
import {TableDataItem} from "@valg-shared/components/table/table/table.component";
import {TableDataItemLoadingStatus, TableSearchField} from "./table.component";
import {TableFilterDialogResultData} from "./valg-filter-dialog/table-filter-dialog.data";

interface SearchQuery {
  searchValue: string,
  searchColumnKeys: string[]
}

export class ValgDatasource<T extends TableDataItem> extends DataSource<T> {
  private _initialData: T[] = [];
  private readonly _dataStream = new BehaviorSubject<T[]>([]);
  private readonly _renderData = new BehaviorSubject<T[]>([]);
  private _searchTerm = '';
  private _pagination?: Pagination;
  private _columnValueFilter?: TableFilterDialogResult;
  private readonly _searchableColumn: Map<string, Column> = new Map<string, Column>();
  private _columnDef: Column[] = [];
  private readonly _secondaryCount: Map<string, number> = new Map<string, number>();
  private _searchCollections: {[key in string]: SearchQuery | undefined} | undefined;
  private _disabledDataManipulation = false;
  private _numberOfCurrentItems = 0;

  constructor(items: T[] = []) {
    super();
    this._initialData = items;
    this._dataStream.subscribe((val) => {
      let processedData: T[] = val;
      if (this._pagination) {
        const newPageIndex = this.calculateStartAndEndIndex(
          this._pagination.currentPage,
          this._pagination.pageSize,
          this.data.length
        );
        processedData = processedData.slice(
          newPageIndex.startIndex,
          newPageIndex.endIndex + 1
        );
      }
      this._renderData.next(processedData);
      this._numberOfCurrentItems = processedData.length;
    });
  }

  private _data: T[] = [];
  private _hasNestedItems = false;
  private _lazyLoad = false;


  isInitialDataEmpty(): boolean {
    return this._initialData.length == 0;
  }

  enableLazyLoad() {
    this._lazyLoad = true;
  }

  disableDataManipulation(){
    this._disabledDataManipulation = true;
  }

  get data(): T[] {
    return this._data;
  }

  get numberOfitemsOnCurrentPage(): number {
    return this._numberOfCurrentItems;
  }

  get hasNestedItems(): boolean {
    return this._hasNestedItems
  }

  set data(data: T[]) {
    if (
      this._initialData.length == 0 &&
      Array.isArray(data) &&
      data.length > 0
    ) {
      this._initialData = data.slice();
    }
    this._data = Array.isArray(data) ? this.flatData(data) : [];
    this.refreshRenderData();
  }

  get activeColumnFilter(): number {
    return this._columnValueFilter?.columns.length ?? 0;
  }

  get dataStream(): BehaviorSubject<T[]> {
    return this._dataStream;
  }

  set columnsDef(columnDef: Column[]) {
    this._columnDef = columnDef;
    columnDef.forEach((x) => {
      if ((x.metadata?.searchable ?? true) && x.propKey) {
        this._searchableColumn.set(x.propKey, x);
      }
    });
  }

  connect(_collectionViewer: CollectionViewer): Observable<readonly T[]> {
    return this._renderData;
  }

  disconnect(_collectionViewer: CollectionViewer): void {
    this._renderData.unsubscribe();
  }

  moveItemInArray(item: CdkDragDrop<T[]> | null, previousIndex?: number, currentIndex?: number): void {
    let arrayCopy = this.data.slice();
    let pageOverhead = 0;
    if (this._pagination) {
      pageOverhead =
        (this._pagination.currentPage - 1) * this._pagination.pageSize;
    }
    if(item !== null){
      previousIndex = item.previousIndex;
      currentIndex = item.currentIndex;
    }
    if(previousIndex !== undefined && currentIndex !== undefined){
      moveItemInArray(
        arrayCopy,
        previousIndex + pageOverhead,
        currentIndex + pageOverhead
      );
      this.data = arrayCopy;
    }
  }
  
  sortData(sort?: Sort): void {
    const sortCol =
      this._columnDef.find((x) => x.title === sort?.id) ??
      this._columnDef.find((x) => x.metadata?.defaultSort !== undefined);
    if (!sortCol || this._disabledDataManipulation) {
      return;
    }
    const direction = sort?.sortDirection ?? sortCol.metadata?.defaultSort;
    const sortFunction = sort?.sortFunction ?? sortCol.metadata?.sortFunction;
    if (direction === undefined || direction == 'none') {
      this.data = this._data.slice();
      return;
    }
    const sortedSet = [ ...this.data ].sort((a, b) =>
      this.dataComparer(a, b, sortCol, direction, sortFunction)
    );
    this.data = sortedSet;
  }

  // eslint-disable-next-line
  columnDataAccessor: (data: T, path: string) => any = (
    data: T,
    path: string
    // eslint-disable-next-line
  ): any => {
    return getValueAtWithArrays(data, path);
  };

  paginationChanged(pagination: Pagination): void {
    this._pagination = pagination;
    this.refreshRenderData();
  }

  refreshRenderData(): void {
    let processedData: T[] = this._data.slice();

    if (this._disabledDataManipulation) {
      this._dataStream.next(processedData);
      return;
    }

    if ((this._searchTerm != null && this._searchTerm !== '') || this._searchCollections) {
      processedData = processedData.filter((obj) =>
        this.searchPrecicate(obj, this._searchTerm, this._searchableColumn)
      );
    }

    if (this._columnValueFilter && this._columnValueFilter.columns.length > 0) {
      processedData = processedData.filter((obj) =>
        this.filterColumnPredicate(obj, this._columnValueFilter!)
      );
    }
    this._dataStream.next(processedData);
  }

  private filterArrayValue(filterColumn: TableFilterDialogResultData, dataCellValue: any[]) {
    const valueArrayAsString = dataCellValue
        .filter((x) => x)
        .map((x) => x.toString());

    return filterColumn.value.every((val) => valueArrayAsString.includes((val as string).toString()));
  }

  private filterStringValue(filterColumn: TableFilterDialogResultData, dataCellValue: any[]) {
    const dataCellStringLowercase = dataCellValue.toString().toLowerCase();

    if (Array.isArray(filterColumn.value)) {
      const flatArray = filterColumn.value.flat();
      const filterValuesLowerCase = flatArray.map(x => (x as string).toString().toLowerCase());
      return filterValuesLowerCase.includes(dataCellStringLowercase);
    }

    if (typeof filterColumn.value === 'string') {
      const filterValueStringLowercase = (filterColumn.value as string).toString().toLowerCase();
      return dataCellStringLowercase.includes(filterValueStringLowercase);
    }

    return false;
  }

  filterColumnPredicate: (
      data: T,
      columnFilter: TableFilterDialogResult
  ) => boolean = (data: T, columnFilter: TableFilterDialogResult): boolean => {
    for (let filterColumn of columnFilter.columns) {
      let dataCellValue = getValueAtWithArrays(data, filterColumn.column.metadata?.filter?.filterPath ?? filterColumn.column.path);
      let result = true;

      const filterValueConverter = filterColumn.column.metadata?.filter?.filterValueConverter;
      if (filterValueConverter) {
        dataCellValue = filterValueConverter(dataCellValue);
      }

      dataCellValue = this.getCheckboxLabelFromQueryValue(dataCellValue,filterColumn)

      if (Array.isArray(dataCellValue)) {
        result = this.filterArrayValue(filterColumn, dataCellValue);
      } else if(dataCellValue == null) {
        result = false;
      } else {
        result = this.filterStringValue(filterColumn, dataCellValue);
      }

      if (!result) {
        return false;
      }
    }

    return true;
  };


  getCheckboxLabelFromQueryValue(dataCellValue:any, filterColumn:any){
    let options = filterColumn.column.metadata?.filter?.filterOptions;
    if (options == undefined) {
      return dataCellValue;
    }
    let optionMatch = options.find((option:any) => {
      return (option.label === dataCellValue)
    })
    if (optionMatch != undefined){
      dataCellValue = optionMatch.value
    }
      return dataCellValue;
  }

  searchPrecicate: (
    data: T,
    searchTerm: string,
    searchableColumn: Map<string, Column>,
  ) => boolean = (
    data: T,
    searchTerm: string,
    searchableColumn: Map<string, Column>,
  ): boolean => {
    let searchValues: string[];
    let dataStr: string;
    if (this._searchCollections) {
      searchValues = Object.keys(this._searchCollections)
          .map(key => this._searchCollections![key]?.searchValue.trim().toLowerCase() ?? '').filter(x => x.length > 0);
      dataStr = this.constructDataStringForSearching(data, searchableColumn, Object.keys(this._searchCollections).flatMap(key => {
        return this._searchCollections![key]?.searchColumnKeys ?? []
      }))
    } else {
      searchValues = [searchTerm.trim().toLowerCase()];
      dataStr = this.constructDataStringForSearching(data, searchableColumn)
    }

    return searchValues.length == 0 || searchValues.every(searchValue => dataStr.includes(searchValue));
  };

  private constructDataStringForSearching(data: T, searchableColumn: Map<string, Column>, searchableColumnPropKeys: string[] = []) {
    return Object.keys(data as unknown as Record<string, unknown>)
      .reduce((currentTerm: string, key: string) => {
        if (searchableColumnPropKeys.length > 0 && !searchableColumnPropKeys.includes(key)) {
          return currentTerm;
        }
        if (searchableColumn.has(key)) {
          var colDef = searchableColumn.get(key);
          var path: any;
          if (colDef?.metadata?.childPath) {
            path = colDef.path + "." + colDef.metadata.childPath;
          } else {
            path = colDef?.path;
          }

          const result = getValueAt(data, path);
          return currentTerm + result + '◬';
        } else {
          return currentTerm;
        }
      }, '')
      .toLowerCase();
  }

  multipleSearch(searchTerm: string, searchField: TableSearchField) {
    this._searchCollections = {
      ...this._searchCollections,
      [searchField.discriminator]: searchTerm ? {
        searchValue: searchField.formatter ? searchField.formatter(searchTerm) : searchTerm,
        searchColumnKeys: searchField.searchableColumnKeys
      } as SearchQuery : undefined
    }
    this.refreshRenderData();
  }

  clearSearchFromMultiple(searchField: any){
    if(this._searchCollections){
      const { [searchField.discriminator]: removedProperty, ...restSearchCollections } = this._searchCollections;
      this._searchCollections = restSearchCollections;
      this.refreshRenderData();
    }
  }

  search(searchTerm: string): void {
    this._searchTerm = searchTerm;
    this.refreshRenderData();
  }

  filterColumnValue(_filter?: TableFilterDialogResult): void {
    this.refreshRenderData();
  }

  updateFilterInformation(filter?: TableFilterDialogResult): void {
    this._columnValueFilter = filter;
  }

  getFilterDialogData(): TableFilterDialogData {
    const data = this.data;

    let result = this._columnDef
        .filter((x) => x.metadata?.filterable)
        .map((column) => {
          const allDataInColumn = data
              .map((d) => this.columnDataAccessor(d, column.metadata?.filter?.filterPath ?? column.path))
              .filter((item) => item)
              .reduce(
                  (acc, val) => {
                    return Array.isArray(val) ? acc.concat(val) : acc.concat([ val ]);
                  },
                  []
              );

          const uniqueItem = [ ...new Set(allDataInColumn) ];

          const preselectedValue =
              this._columnValueFilter?.columns.find(
                  (x) => x.column.title === column.title
              )?.value ?? [];

          return {
            column: column,
            value: uniqueItem,
            preSelected: preselectedValue,
          } as TableFilterDialogColumnData;
        });

    return {
      columns: result,
    };
  }

  calculateStartAndEndIndex(
    currentPage: number,
    pageSize: number,
    totalItems: number
  ): { startIndex: number; endIndex: number } {
    let startIndex = (currentPage - 1) * pageSize;
    let endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);
    return { startIndex, endIndex };
  }

  calculateSecondaryCount(): Map<string, number> {
    this._columnDef
      .filter((column) => column.metadata?.secondaryCount != undefined)
      .forEach((column) => {
        const columnCount = this.data.filter((item) => {
          if (column.metadata?.secondaryCount) {
            return column.metadata?.secondaryCount(item);
          }
          return false;
        }).length;
        this._secondaryCount.set(
          column.metadata?.secondaryCountDescription ?? '',
          columnCount
        );
      });
    return this._secondaryCount;
  }

  private dataComparer(a: T, b: T, sortCol: Column, direction: ValgSortDir, comparer?: ValgSortFunc) {
    let valueA = this.columnDataAccessor(a, sortCol.path);
    let valueB = this.columnDataAccessor(b, sortCol.path);

    let comparatorResult: number;
    if (comparer){
      comparatorResult = comparer(valueA, valueB, direction);
    } else {
      comparatorResult = ValgDatasource.defaultValueComparer(valueA, valueB);
    }
    if (comparatorResult === 0 && sortCol.metadata?.secondarySort !== undefined){
      valueA = this.columnDataAccessor(a, sortCol.metadata.secondarySort.path);
      valueB = this.columnDataAccessor(b, sortCol.metadata.secondarySort.path);
      comparatorResult = sortCol.metadata.secondarySort.sortFunction(valueA, valueB, sortCol.metadata.secondarySort.direction ?? direction);
      if (comparatorResult != 0){
        return comparatorResult * ((sortCol.metadata.secondarySort.direction ?? direction) == 'asc' ? 1 : -1);
      }
    }

    return comparatorResult * (direction == 'asc' ? 1 : -1);
  }
  // eslint-disable-next-line
  private static defaultValueComparer (valueA: any, valueB: any){
    // If both valueA and valueB exist (truthy), then compare the two. Otherwise, check if
    // one value exists while the other doesn't. In this case, existing value should come last.
    // This avoids inconsistent results when comparing values to undefined/null.
    // If neither value exists, return 0 (equal).
    let comparatorResult = 0;
    if (valueA != null && valueB != null) {
      // if multivalue column, longer win
      if (Array.isArray(valueA) && Array.isArray(valueB)) {
        comparatorResult = valueA.length > valueB.length ? 1 : -1;
      }
      // Check if one value is greater than the other; if equal, comparatorResult should remain 0.
      else if (valueA > valueB) {
        comparatorResult = 1;
      } else if (valueA < valueB) {
        comparatorResult = -1;
      }
    } else if (valueA != null) {
      comparatorResult = 1;
    } else if (valueB != null) {
      comparatorResult = -1;
    }
    return comparatorResult
  }

  public updateNestedDataAndRefresh(updateAll: boolean = false) {
    this._data = this.flatData(this._data, updateAll);
    this.refreshRenderData();
  }

  private flatData(data: T[], updateAll: boolean = false) {
    const flattedData = data.flatMap(dataItem => {
      let nestedItems: T[] = []
      if (!this._lazyLoad ||
          dataItem.loadingStatus === TableDataItemLoadingStatus.Loading ||
          (updateAll && dataItem.loadingStatus !== TableDataItemLoadingStatus.Done)) {
        nestedItems = dataItem.children?.map(dataChildItem => {
          dataChildItem.loadingStatus = this._lazyLoad ? TableDataItemLoadingStatus.Done : undefined;
          return dataChildItem as T
        }) ?? []
        dataItem.loadingStatus = this._lazyLoad ? TableDataItemLoadingStatus.Done : undefined;
      }

      const flatItemList = dataItem.loadingStatus === TableDataItemLoadingStatus.Done ? [dataItem] : ([ { ...dataItem, children: dataItem.children ?? [] } ] as T[])
      return flatItemList.concat(nestedItems);
    });
    this._hasNestedItems = data.some(x => x.children && x.children.length > 0);
    return flattedData;
  }

  public getProcessedExportData(): T[]{
    let processedData = this._dataStream.getValue();
    this._columnDef.forEach(column => {
      if (column.metadata?.dataExportTranslateFunction) {
        processedData = processedData.map(item => {
          return {...item, [column.path]: column.metadata?.dataExportTranslateFunction?.((item as any)[column.path])}
        })
      }
    })
    return processedData;
  }
}
